/**
 * iter161 — Page Pointage (employé).
 *
 * L'utilisateur connecté voit UNIQUEMENT ses propres pointages.
 * - Sélection période (semaine en cours / mois / personnalisé)
 * - Cartes haut : heures travaillées / supp / manquantes / retards
 * - Tableau jour par jour (entrées/sorties, retard, manque/supp)
 * - Vue semaine (agrégat)
 * - Jours fériés genevois grisés
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Clock, TrendingUp, AlertTriangle, Calendar, FileWarning, Settings, Palmtree, CheckCircle2, LogIn, Loader2, Flag, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtHM(min) {
  if (min == null) return "—";
  const s = min < 0 ? "-" : "";
  const m = Math.abs(Math.round(min));
  return `${s}${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function motifLabel(m) {
  return { maladie: "Maladie", accident: "Accident", vacances: "Vacances", sans_solde: "Sans solde", autre: "Autre" }[m] || m;
}

// iter196 — Emoji + couleur badge par type d'absence
function congeStyle(type) {
  return {
    vacances:   { emoji: "🌴", classes: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    maladie:    { emoji: "🏥", classes: "bg-rose-100 text-rose-800 border-rose-300" },
    accident:   { emoji: "🚑", classes: "bg-orange-100 text-orange-800 border-orange-300" },
    sans_solde: { emoji: "⚪", classes: "bg-gray-100 text-gray-800 border-gray-300" },
    autre:      { emoji: "📌", classes: "bg-blue-100 text-blue-800 border-blue-300" },
  }[type] || { emoji: "📌", classes: "bg-blue-100 text-blue-800 border-blue-300" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { weekday: "short", day: "2-digit", month: "2-digit" });
}

function weekDates(offset = 0) {
  const today = new Date();
  const dow = (today.getDay() + 6) % 7; // 0=Mon
  const monday = new Date(today);
  monday.setDate(today.getDate() - dow + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: monday.toISOString().slice(0, 10), to: sunday.toISOString().slice(0, 10) };
}

function monthDates() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

export default function Pointage() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter161.4 — Justification d'absence
  const [justifyDay, setJustifyDay] = useState(null); // {date, ...}
  const [justifyForm, setJustifyForm] = useState({ motif: "maladie", commentaire: "" });
  const [busy, setBusy] = useState(false);
  // iter161.6 — Signalement erreur pointage
  const [signalEvt, setSignalEvt] = useState(null);
  const [signalForm, setSignalForm] = useState({ time: "", commentaire: "" });
  // iter161.7 — Module Congés
  const [showConges, setShowConges] = useState(false);
  const [mesConges, setMesConges] = useState([]);
  const [congeForm, setCongeForm] = useState({ date_from: "", date_to: "", type: "vacances", commentaire: "" });

  const { from, to } = useMemo(() => {
    if (period === "week") return weekDates(0);
    if (period === "prev_week") return weekDates(-1);
    return monthDates();
  }, [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/pointage/me", { params: { date_from: from, date_to: to } }),
        api.get("/pointage/me/settings"),
      ]);
      setData(r1.data);
      setSettings(r2.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const submitJustify = async () => {
    if (!justifyDay) return;
    setBusy(true);
    try {
      await api.post("/pointage/me/justifications", {
        date: justifyDay.date,
        motif: justifyForm.motif,
        commentaire: justifyForm.commentaire,
      });
      toast.success("Justification envoyée — en attente de validation admin");
      setJustifyDay(null);
      setJustifyForm({ motif: "maladie", commentaire: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // iter161.6 — Signalement
  const submitSignal = async () => {
    if (!signalEvt) return;
    setBusy(true);
    try {
      await api.post("/pointage/me/signalements", {
        pointage_id: signalEvt.id,
        expected_time: signalForm.time,
        commentaire: signalForm.commentaire,
      });
      toast.success("Signalement envoyé à l'admin");
      setSignalEvt(null);
      setSignalForm({ time: "", commentaire: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // iter161.7 — Charger congés à l'ouverture
  const loadConges = useCallback(async () => {
    try {
      const { data } = await api.get("/pointage/me/conges");
      setMesConges(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }, []);
  useEffect(() => { if (showConges) loadConges(); }, [showConges, loadConges]);

  const submitConge = async () => {
    setBusy(true);
    try {
      await api.post("/pointage/me/conges", congeForm);
      toast.success("Demande envoyée à l'admin");
      setCongeForm({ date_from: "", date_to: "", type: "vacances", commentaire: "" });
      loadConges();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const cancelConge = async (cid) => {
    if (!window.confirm("Annuler cette demande ?")) return;
    try {
      await api.delete(`/pointage/me/conges/${cid}`);
      toast.success("Demande annulée");
      loadConges();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const totals = useMemo(() => {
    if (!data) return { worked: 0, supp: 0, manque: 0, retard: 0, to_justify: 0 };
    const t = (data.weeks || []).reduce(
      (acc, w) => ({
        worked: acc.worked + w.minutes_worked,
        supp: acc.supp + w.heures_supp_min,
        manque: acc.manque + w.heures_manquantes_min,
        retard: acc.retard + w.retard_min,
        to_justify: acc.to_justify + w.n_to_justify,
      }),
      { worked: 0, supp: 0, manque: 0, retard: 0, to_justify: 0 },
    );
    return t;
  }, [data]);

  return (
    <div className="space-y-4" data-testid="pointage-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-7 h-7 text-emerald-600" /> Pointage
          </h1>
          <p className="text-sm text-gray-500">
            Tes pointages, heures et écarts par rapport à ton horaire de référence.
          </p>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList>
            <TabsTrigger value="prev_week" data-testid="pointage-tab-prev-week">Sem. dernière</TabsTrigger>
            <TabsTrigger value="week" data-testid="pointage-tab-week">Cette semaine</TabsTrigger>
            <TabsTrigger value="month" data-testid="pointage-tab-month">Ce mois</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant="outline" onClick={() => setShowConges(true)} data-testid="pointage-conges-btn">
          <Palmtree className="w-4 h-4 mr-1" /> Mes congés
        </Button>
      </header>

      {!settings?.timemoto_id && (
        <Card className="p-4 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="pointage-no-timemoto">
          <Settings className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Aucun ID TimeMoto associé à ton compte.</p>
            <p>Demande à l&apos;admin d&apos;associer ton ID badge — sans ça, tes pointages ne s&apos;affichent pas.</p>
          </div>
        </Card>
      )}

      {/* iter161.3 — Bandeau "Aujourd'hui" en évidence si pointage du jour */}
      {data?.today && data.today.n_events > 0 && (
        <Card className="p-4 border-emerald-300 bg-emerald-50/60 flex items-center justify-between gap-3 flex-wrap" data-testid="pointage-today-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              {data.today.is_currently_in ? (
                <LogIn className="w-5 h-5 text-emerald-700" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              )}
            </div>
            <div>
              <div className="font-semibold text-emerald-900">
                {data.today.is_currently_in ? "Tu es au travail" : "Tu as terminé ta journée"}
                <span className="ml-2 text-xs font-normal text-emerald-700">·  aujourd&apos;hui</span>
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">
                {data.today.n_events} événement{data.today.n_events > 1 ? "s" : ""} ·
                {" "}dernier pointage à <span className="font-mono font-semibold">{data.today.last_event_time}</span>
                {" "}({data.today.last_event_type === "in" ? "entrée" : "sortie"})
                {data.today.minutes_worked > 0 && (
                  <span className="ml-2">· déjà <span className="font-mono font-semibold">{fmtHM(data.today.minutes_worked)}</span> travaillées</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <section className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiCard testid="kpi-worked" label="Heures travaillées" value={fmtHM(totals.worked)} icon={Clock} color="emerald" />
        <KpiCard testid="kpi-supp" label="Heures supp." value={fmtHM(totals.supp)} icon={TrendingUp} color="blue" />
        <KpiCard testid="kpi-manque" label="Heures manquantes" value={fmtHM(totals.manque)} icon={AlertTriangle} color="orange" />
        <KpiCard testid="kpi-retard" label="Retard cumulé" value={fmtHM(totals.retard)} icon={Clock} color="red" />
        <KpiCard testid="kpi-to-justify" label="Jours à justifier" value={String(totals.to_justify)} icon={FileWarning} color="violet" />
        <KpiCard
          testid="kpi-conges"
          label="Congés restants"
          value={data?.conges?.solde_disponible != null ? `${data.conges.solde_disponible} j` : "—"}
          icon={Palmtree}
          color={data?.conges?.solde_disponible < 0 ? "red" : "emerald"}
        />
      </section>

      {/* Vue par semaine */}
      <section className="space-y-3" data-testid="pointage-weeks">
        {loading && <p className="text-sm text-gray-500">Chargement…</p>}
        {!loading && (data?.weeks || []).map((w) => (
          <Card key={w.key} className="p-4" data-testid={`pointage-week-${w.key}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                Semaine {w.key}
              </h2>
              <div className="flex items-center gap-3 text-xs">
                <span><span className="text-gray-500">Travaillé :</span> <span className="font-mono font-semibold">{fmtHM(w.minutes_worked)}</span></span>
                {w.heures_supp_min > 0 && <Badge className="bg-blue-100 text-blue-800 border-blue-200">+{fmtHM(w.heures_supp_min)} supp</Badge>}
                {w.heures_manquantes_min > 0 && <Badge className="bg-orange-100 text-orange-800 border-orange-200">−{fmtHM(w.heures_manquantes_min)}</Badge>}
                {w.retard_min > 0 && <Badge className="bg-red-100 text-red-800 border-red-200">Retard {fmtHM(w.retard_min)}</Badge>}
                {w.n_conges > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid={`week-conges-${w.key}`}>🌴 {w.n_conges} j vacances</Badge>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="py-1.5 px-2">Date</th>
                    <th className="py-1.5 px-2">Entrées / Sorties</th>
                    <th className="py-1.5 px-2 text-right">Travaillé</th>
                    <th className="py-1.5 px-2 text-right">Retard</th>
                    <th className="py-1.5 px-2 text-right">Écart</th>
                    <th className="py-1.5 px-2">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {w.days.map((d) => {
                    const rowCls = d.is_today
                      ? "border-b border-emerald-200 bg-emerald-50/60 ring-1 ring-emerald-300"
                      : d.conge
                        ? "border-b border-emerald-100 bg-emerald-50/40"
                        : d.is_ferie
                          ? "border-b border-gray-50 bg-gray-50"
                          : (d.is_future || d.is_pre_tracking)
                            ? "border-b border-gray-50 opacity-50"
                            : "border-b border-gray-50";
                    return (
                      <tr key={d.date} className={rowCls} data-testid={`pointage-day-${d.date}`}>
                        <td className="py-2 px-2 whitespace-nowrap font-mono text-xs">
                          {fmtDate(d.date)}
                          {d.is_today && <span className="ml-1 text-[10px] font-bold text-emerald-700">· AUJOURD&apos;HUI</span>}
                          {d.is_ferie && <span className="ml-1 text-[10px] text-gray-500">· {d.ferie_label}</span>}
                          {d.is_pre_tracking && <span className="ml-1 text-[10px] text-gray-400">· avant suivi</span>}
                          {d.conge && (() => {
                            const s = congeStyle(d.conge.type);
                            return <span className={`ml-1 text-[10px] font-semibold`}>· {s.emoji} {motifLabel(d.conge.type)}</span>;
                          })()}
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {d.events && d.events.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                              {d.events.map((e) => (
                                <button
                                  key={e.id}
                                  type="button"
                                  onClick={() => { setSignalEvt(e); setSignalForm({ time: e.time, commentaire: "" }); }}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] hover:bg-amber-50 hover:ring-1 hover:ring-amber-300 transition-colors group"
                                  title="Signaler une erreur sur ce pointage"
                                  data-testid={`event-${e.id}`}
                                >
                                  {e.type === "in" ? (
                                    <span className="text-emerald-700 font-semibold">→ {e.time}</span>
                                  ) : (
                                    <span className="text-red-700 font-semibold">← {e.time}</span>
                                  )}
                                  <Flag className="w-2.5 h-2.5 text-gray-300 group-hover:text-amber-600" />
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">
                              {d.is_future ? "—" : d.is_pre_tracking ? "—" : (d.is_work_day ? "Aucun pointage" : "—")}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{d.minutes_worked > 0 ? fmtHM(d.minutes_worked) : "—"}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">
                          {d.retard_min > 0 ? <span className="text-red-700">+{fmtHM(d.retard_min)}</span> : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">
                          {d.heures_supp_min > 0 && <span className="text-blue-700">+{fmtHM(d.heures_supp_min)}</span>}
                          {d.heures_manquantes_min > 0 && <span className="text-orange-700">−{fmtHM(d.heures_manquantes_min)}</span>}
                          {(d.heures_supp_min === 0 && d.heures_manquantes_min === 0 && d.is_tracked && !d.is_today && d.minutes_worked > 0) && <span className="text-emerald-600">OK</span>}
                          {(!d.is_tracked || d.is_today) && <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-2 px-2 text-xs">
                          {d.is_today && d.minutes_worked > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">En cours</Badge>
                          )}
                          {d.is_future && <Badge variant="secondary" className="bg-gray-100 text-gray-500">À venir</Badge>}
                          {d.is_pre_tracking && <Badge variant="secondary" className="bg-gray-100 text-gray-500">Avant suivi</Badge>}
                          {d.conge && (() => {
                            const s = congeStyle(d.conge.type);
                            return (
                              <Badge className={`${s.classes}`} data-testid={`day-conge-${d.date}`}>
                                {s.emoji} {motifLabel(d.conge.type)} validé
                              </Badge>
                            );
                          })()}
                          {d.justification && d.justification.status === "en_attente" && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`day-justif-pending-${d.date}`}>
                              ⏳ En attente · {motifLabel(d.justification.motif)}
                            </Badge>
                          )}
                          {d.justification && d.justification.status === "validee" && (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`day-justif-valid-${d.date}`}>
                              ✓ {motifLabel(d.justification.motif)}
                            </Badge>
                          )}
                          {d.needs_justification && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-50"
                              onClick={() => { setJustifyDay(d); setJustifyForm({ motif: "maladie", commentaire: "" }); }}
                              data-testid={`day-justify-btn-${d.date}`}
                            >
                              Justifier
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
        {!loading && (data?.weeks || []).length === 0 && (
          <Card className="p-6 text-center text-sm text-gray-500" data-testid="pointage-empty">
            Aucun pointage sur cette période.
          </Card>
        )}
      </section>

      {settings && (
        <Card className="p-4 text-xs text-gray-500 space-y-2" data-testid="pointage-settings-info">
          <div>
            <div className="font-semibold text-gray-700 mb-1">Horaire de référence</div>
            <div>
              Lun–Ven : {settings.horaire_reference?.morning_start}–{settings.horaire_reference?.morning_end} · {settings.horaire_reference?.afternoon_start}–{settings.horaire_reference?.afternoon_end}
              {" "}({settings.horaire_reference?.target_hours_day} h/jour)
            </div>
            <div className="mt-1">ID TimeMoto : <span className="font-mono">{settings.timemoto_id || "—"}</span></div>
          </div>
          {data?.conges && (
            <div className="pt-2 border-t border-gray-100">
              <div className="font-semibold text-gray-700 mb-1">Congés {new Date().getFullYear()}</div>
              <div className="font-mono">
                {data.conges.quota_annuel} (quota) + {data.conges.report} (report N−1) − {data.conges.pris_avant_demarrage} (déjà pris) ={" "}
                <span className={`font-bold ${data.conges.solde_disponible < 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {data.conges.solde_disponible} j restants
                </span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* iter161.4 — Dialog "Justifier une absence" */}
      <Dialog open={!!justifyDay} onOpenChange={(o) => !o && setJustifyDay(null)}>
        <DialogContent className="max-w-md" data-testid="justify-dialog">
          <DialogHeader>
            <DialogTitle>Justifier l&apos;absence du {justifyDay ? new Date(justifyDay.date).toLocaleDateString("fr-CH", { weekday: "long", day: "2-digit", month: "long" }) : ""}</DialogTitle>
            <DialogDescription>
              Choisis un motif. La demande sera envoyée à l&apos;admin pour validation. Statut initial : <span className="font-semibold text-amber-700">en attente</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Motif</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { v: "maladie", l: "🤒 Maladie" },
                  { v: "accident", l: "🚑 Accident" },
                  { v: "vacances", l: "🌴 Vacances" },
                  { v: "autre", l: "❓ Autre" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setJustifyForm({ ...justifyForm, motif: opt.v })}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      justifyForm.motif === opt.v
                        ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`justify-motif-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Commentaire <span className="text-gray-400">(facultatif)</span></Label>
              <Textarea
                value={justifyForm.commentaire}
                onChange={(e) => setJustifyForm({ ...justifyForm, commentaire: e.target.value })}
                placeholder="Précision pour l'admin…"
                className="mt-1"
                rows={3}
                data-testid="justify-commentaire"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJustifyDay(null)} data-testid="justify-cancel">Annuler</Button>
            <Button onClick={submitJustify} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="justify-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* iter161.6 — Dialog signalement erreur pointage */}
      <Dialog open={!!signalEvt} onOpenChange={(o) => !o && setSignalEvt(null)}>
        <DialogContent className="max-w-md" data-testid="signal-dialog">
          <DialogHeader>
            <DialogTitle>Signaler une erreur</DialogTitle>
            <DialogDescription>
              Pointage actuel : <span className="font-mono font-semibold">{signalEvt?.date} · {signalEvt?.time} · {signalEvt?.type === "in" ? "Entrée" : "Sortie"}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Heure réelle (corrigée)</Label>
              <input type="time" value={signalForm.time} onChange={(e) => setSignalForm({ ...signalForm, time: e.target.value })} className="mt-1 w-full border rounded-md h-9 px-2 font-mono" data-testid="signal-time" />
            </div>
            <div>
              <Label className="text-sm">Commentaire</Label>
              <Textarea value={signalForm.commentaire} onChange={(e) => setSignalForm({ ...signalForm, commentaire: e.target.value })} placeholder="Précise ce qui s'est passé..." rows={3} className="mt-1" data-testid="signal-commentaire" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignalEvt(null)}>Annuler</Button>
            <Button onClick={submitSignal} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="signal-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Envoyer le signalement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter161.7 — Dialog "Mes congés" */}
      <Dialog open={showConges} onOpenChange={setShowConges}>
        <DialogContent className="max-w-2xl" data-testid="conges-dialog">
          <DialogHeader>
            <DialogTitle>Mes congés {new Date().getFullYear()}</DialogTitle>
            <DialogDescription>
              Solde restant : <span className="font-bold text-emerald-700">{data?.conges?.solde_disponible ?? "—"} j</span>
              {" "}· déjà validés : {data?.conges?.valides_depuis ?? 0} j
            </DialogDescription>
          </DialogHeader>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-emerald-900 mb-2">Nouvelle demande</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
              <div>
                <Label className="text-xs">Du</Label>
                <input type="date" value={congeForm.date_from} onChange={(e) => setCongeForm({ ...congeForm, date_from: e.target.value, date_to: congeForm.date_to || e.target.value })} className="mt-1 w-full border rounded-md h-9 px-2 text-sm" data-testid="conge-from" />
              </div>
              <div>
                <Label className="text-xs">Au</Label>
                <input type="date" value={congeForm.date_to} onChange={(e) => setCongeForm({ ...congeForm, date_to: e.target.value })} className="mt-1 w-full border rounded-md h-9 px-2 text-sm" data-testid="conge-to" />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select value={congeForm.type} onChange={(e) => setCongeForm({ ...congeForm, type: e.target.value })} className="mt-1 w-full border rounded-md h-9 px-2 text-sm" data-testid="conge-type">
                  <option value="vacances">Vacances (débit solde)</option>
                  <option value="sans_solde">Sans solde</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button size="sm" onClick={submitConge} disabled={busy || !congeForm.date_from || !congeForm.date_to} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="conge-submit">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Envoyer
                </Button>
              </div>
            </div>
            <input value={congeForm.commentaire} onChange={(e) => setCongeForm({ ...congeForm, commentaire: e.target.value })} placeholder="Commentaire (facultatif)" className="w-full border rounded-md h-9 px-2 text-sm" data-testid="conge-commentaire" />
          </div>
          <div className="max-h-72 overflow-y-auto mt-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-600">
                <tr>
                  <th className="py-1.5 px-2 text-left">Période</th>
                  <th className="py-1.5 px-2 text-left">Jours</th>
                  <th className="py-1.5 px-2 text-left">Type</th>
                  <th className="py-1.5 px-2 text-left">Statut</th>
                  <th className="py-1.5 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {mesConges.length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-center text-gray-400 text-xs">Aucune demande</td></tr>
                )}
                {mesConges.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100" data-testid={`conge-row-${c.id}`}>
                    <td className="py-1.5 px-2 font-mono text-xs">{c.date_from} → {c.date_to}</td>
                    <td className="py-1.5 px-2 font-mono">{c.jours_ouvres}</td>
                    <td className="py-1.5 px-2 capitalize">{c.type}</td>
                    <td className="py-1.5 px-2">
                      {c.status === "en_attente" && <Badge className="bg-amber-100 text-amber-800 border-amber-200">⏳ En attente</Badge>}
                      {c.status === "validee" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ Validée</Badge>}
                      {c.status === "refusee" && <Badge className="bg-red-100 text-red-800 border-red-200">✗ Refusée</Badge>}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      {c.status === "en_attente" && (
                        <Button size="sm" variant="ghost" onClick={() => cancelConge(c.id)} className="text-red-600 h-7 px-2" data-testid={`conge-cancel-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConges(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testid }) {
  const colorMap = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    blue: "border-blue-300 bg-blue-50 text-blue-900",
    orange: "border-orange-300 bg-orange-50 text-orange-900",
    red: "border-red-300 bg-red-50 text-red-900",
    violet: "border-violet-300 bg-violet-50 text-violet-900",
  };
  return (
    <Card className={`p-3 border ${colorMap[color] || ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide opacity-70">{label}</span>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="text-2xl font-mono font-bold mt-1">{value}</div>
    </Card>
  );
}
